'use client';

import {
  ReactNode, useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@utils/cn';
import { Bars3Icon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { TwoColumnLayoutContext } from './TwoColumnLayoutContext';

interface TwoColumnLayoutProps {
  leftSidebar?: ReactNode;
  mainContent: ReactNode;
  rightSidebar?: ReactNode;
  showLeftToggle?: boolean;
  className?: string;
}

export const TwoColumnLayout = (props: TwoColumnLayoutProps) => {
  const {
    leftSidebar,
    mainContent,
    rightSidebar,
    showLeftToggle = true,
    className,
  } = props;

  // Desktop sidebar starts open, mobile starts closed
  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [mounted, setMounted] = useState(false);

  const sidebarRef = useRef<HTMLElement>(null);
  const toggleLeftSidebar = useCallback(() => {
    setIsLeftOpen((prev) => !prev);
  }, []);

  const contextValue = useMemo(
    () => ({ isLeftOpen, toggleLeftSidebar }),
    [isLeftOpen, toggleLeftSidebar],
  );

  useEffect(() => { setMounted(true); }, []);

  // Notify listeners (e.g. MainNavbar) of the current sidebar state.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sidebar-state-change', { detail: { isOpen: isLeftOpen } }));
  }, [isLeftOpen]);

  // Collapse sidebar when switching to mobile view
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile) {
        setIsLeftOpen(false);
      } else {
        setIsLeftOpen(true);
      }
    };

    // Set initial state based on screen size
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      setIsLeftOpen(false);
    }

    // Only listen to resize events, not state changes
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array - only run once on mount

  // Close sidebar on mobile with Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isLeftOpen && window.innerWidth < 1024) {
        setIsLeftOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLeftOpen]);

  // Listen for external toggle-sidebar / close-sidebar events
  useEffect(() => {
    const handleToggle = () => toggleLeftSidebar();
    const handleClose = () => setIsLeftOpen(false);
    window.addEventListener('toggle-sidebar', handleToggle);
    window.addEventListener('close-sidebar', handleClose);
    return () => {
      window.removeEventListener('toggle-sidebar', handleToggle);
      window.removeEventListener('close-sidebar', handleClose);
    };
  }, [toggleLeftSidebar]);
  // Close sidebar on mobile when tapping outside the sidebar element (works even through Modal overlays)
  useEffect(() => {
    if (!isLeftOpen) return undefined;
    const handleOutsideTouch = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      // Skip if the tap is on the toggle button (it handles its own toggle via the toggle-sidebar event)
      if (target && (target as HTMLElement).closest('[data-sidebar-toggle]')) return;
      if (window.innerWidth < 1024 && sidebarRef.current && target && !sidebarRef.current.contains(target as Node)) {
        setIsLeftOpen(false);
      }
    };
    document.addEventListener('touchend', handleOutsideTouch);
    return () => {
      document.removeEventListener('touchend', handleOutsideTouch);
    };
  }, [isLeftOpen]);


  // Prevent body scroll on mobile when sidebar is open
  // Only apply when not inside a modal (check for modal ancestor to avoid scroll conflicts)
  useEffect(() => {
    const isInsideModal = !!document.querySelector('[role="dialog"]');
    if (window.innerWidth < 1024 && isLeftOpen && !isInsideModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLeftOpen]);

  const hasLeftSidebar = !!leftSidebar;
  const hasRightSidebar = !!rightSidebar;

  // Keep the globally-mounted header aligned with the main content start.
  useEffect(() => {
    const root = document.documentElement;
    const updateHeaderOffset = () => {
      if (!hasLeftSidebar) {
        root.style.removeProperty('--two-column-header-offset');
        window.dispatchEvent(new CustomEvent('two-column-header-offset-change', {
          detail: { offset: '' },
        }));
        return;
      }

      const isMobile = window.innerWidth < 1024;
      let offset = '';

      if (isLeftOpen) {
        offset = isMobile ? '280px' : '20%';
      } else if (!isMobile) {
        offset = '64px';
      }

      root.style.setProperty('--two-column-header-offset', offset);
      window.dispatchEvent(new CustomEvent('two-column-header-offset-change', {
        detail: { offset },
      }));
    };

    updateHeaderOffset();
    window.addEventListener('resize', updateHeaderOffset);

    return () => {
      window.removeEventListener('resize', updateHeaderOffset);
      root.style.removeProperty('--two-column-header-offset');
      window.dispatchEvent(new CustomEvent('two-column-header-offset-change', {
        detail: { offset: '' },
      }));
    };
  }, [hasLeftSidebar, isLeftOpen]);

  return (
    <TwoColumnLayoutContext.Provider value={contextValue}>
      <div className={cn('two-column-with-sidebar relative flex h-[calc(100dvh-var(--header-height,80px))] overflow-hidden', className)}>
        {/* Mobile overlay backdrop */}
        {isLeftOpen && (
          <div
            className="pointer-events-auto fixed inset-0 z-[55] bg-ink/50 transition-opacity lg:hidden"
            style={{ left: '280px', right: 0 }}
            onClick={() => setIsLeftOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Left sidebar — rendered via createPortal to document.body directly,
             bypassing #headlessui-portal-root so Headless UI's useInertOthers
             cannot mark it as inert when a Dialog/Modal opens. */}
        {hasLeftSidebar && mounted && createPortal(
          <aside
            ref={sidebarRef}
            className={cn(
              'fixed left-0 z-[60] transform bg-surface transition-all duration-300 ease-in-out',
              // Mobile: hidden when closed, full width when open
              isLeftOpen ? 'w-[280px] translate-x-0' : '-translate-x-full lg:translate-x-0',
              // Desktop: collapsed (64px) or expanded (20%)
              'lg:z-[60]',
              isLeftOpen ? 'lg:w-[20%]' : 'lg:w-[64px]',
            )}
            style={{
              top: 'var(--unpaid-alert-height, 0px)',
              height: 'calc(100dvh - var(--unpaid-alert-height, 0px))',
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div
              className="flex h-full flex-col"
              onClick={(e) => {
                if (window.innerWidth < 1024 && isLeftOpen && (e.target as HTMLElement).closest('a')) {
                  setIsLeftOpen(false);
                }
              }}
            >
              {/* Hamburger button inside sidebar */}
              {showLeftToggle && (
                <div className="flex items-center justify-between px-4 py-2">
                  <button
                    type="button"
                    onClick={toggleLeftSidebar}
                    className={cn(
                      'flex size-10 items-center justify-center rounded-lg transition-colors',
                      'hover:bg-surface-200',
                      'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                    )}
                    aria-label={isLeftOpen ? 'Close sidebar' : 'Open sidebar'}
                    aria-expanded={isLeftOpen}
                  >
                    <Bars3Icon className="size-6 text-ink" />
                  </button>

                  {isLeftOpen && (
                    <button
                      type="button"
                      onClick={toggleLeftSidebar}
                      className={cn(
                        'flex size-10 items-center justify-center rounded-lg transition-colors',
                        'hover:bg-surface-200',
                        'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                      )}
                      aria-label="Collapse sidebar"
                    >
                      <ChevronDoubleLeftIcon className="size-5 text-ink" />
                    </button>
                  )}
                </div>
              )}

              {/* Sidebar content */}
              <div className={cn(
                'flex-1 overflow-hidden transition-opacity duration-300',
                isLeftOpen ? 'opacity-100' : 'opacity-0 lg:opacity-0',
              )}
              >
                {leftSidebar}
              </div>
            </div>
          </aside>,
          document.body,
        )}

        {/* Main content */}
        <main
          className={cn(
            'flex-1 min-w-0 h-full overflow-y-auto transition-all duration-300',
            // Mobile: no sidebar offset (sidebar is hidden/overlay)
            'ml-0',
            // Desktop: account for sidebar width
            'lg:ml-[64px]',
            isLeftOpen && 'lg:ml-[20%]',
          )}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorY: 'contain',
            touchAction: 'pan-y',
          }}
        >
          {mainContent}
        </main>

        {/* Right sidebar (if needed in future) */}
        {hasRightSidebar && (
          <aside className="hidden bg-surface lg:block lg:w-[280px]">
            <div className="h-full overflow-y-auto">
              {rightSidebar}
            </div>
          </aside>
        )}
      </div>
    </TwoColumnLayoutContext.Provider>
  );
};
